/**
 * Obra de REFERENCIA: una obra que solo trae su árbol de partidas (sin conceptos), elegida por el usuario
 * como estructura de la hoja del plano. Reglas puras del árbol (sin red, sin crypto: se usan también en la web).
 * Spec: docs/_archivo/internos/spec-obra-referencia-partidas.md
 */
import type { PlanoAgrupacion } from './domain.js';

/** Una obra que solo trae su árbol de partidas (sin conceptos). */
export interface ObraReferencia {
  id: string;
  nombre: string;
  origen: 'semilla' | 'm1';
  descripcion?: string | null;
  /** Lista PLANA, N niveles vía padreId, en el orden del documento. */
  nodos: NodoObraReferencia[];
}

export interface NodoObraReferencia {
  id: string;
  padreId: string | null;
  /** Tal como viene ("CIMENTACIONES"). */
  nombre: string;
  /** Posición entre hermanos (0..n). */
  orden: number;
  /** Clave de la partida si la fuente la trae. */
  clave?: string | null;
  /** SOLO semilla: idx de la fila group en seed-data.json (la vara lo usa para la verdad). */
  origenIdx?: number;
}

/** Lo que se lista (sin nodos). */
export interface ObraReferenciaResumen {
  id: string;
  nombre: string;
  origen: 'semilla' | 'm1';
  /** Partidas que pueden recibir conceptos: todas menos la raíz única (partida principal). */
  partidasElegibles: number;
  /** Profundidad visible. */
  niveles: number;
  huella: string;
}

export interface ObraReferenciaDTO extends ObraReferencia {
  huella: string;
}

/** Tope de nodos y de profundidad que acepta el validador. */
export const OBRA_REF_MAX_NODOS = 2000;
export const OBRA_REF_MAX_PROFUNDIDAD = 8;

/** FNV-1a de 32 bits en hex (8 caracteres). Sync y sin crypto: detecta cambios, no es seguridad. */
export function huellaCorta(texto: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < texto.length; i++) {
    h ^= texto.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** Clave de una descripción: trim + espacios colapsados + minúsculas. */
export function claveDeDescripcion(texto: string): string {
  return texto.replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Hijos por padre (clave '' = raíces), ordenados por `orden` y, a igualdad, por posición en la lista. */
function hijosPorPadre(o: ObraReferencia): Map<string, NodoObraReferencia[]> {
  const pos = new Map<string, number>();
  o.nodos.forEach((n, i) => pos.set(n.id, i));
  const m = new Map<string, NodoObraReferencia[]>();
  for (const n of o.nodos) {
    const k = n.padreId ?? '';
    const arr = m.get(k) ?? [];
    arr.push(n);
    m.set(k, arr);
  }
  for (const arr of m.values()) arr.sort((a, b) => a.orden - b.orden || (pos.get(a.id) ?? 0) - (pos.get(b.id) ?? 0));
  return m;
}

/** [] = válida; si no, los motivos. */
export function validarObraReferencia(o: ObraReferencia): string[] {
  const errores: string[] = [];
  if (!o.id) errores.push('La obra no trae id.');
  if (o.nodos.length === 0) errores.push('La obra no trae partidas.');
  if (o.nodos.length > OBRA_REF_MAX_NODOS) errores.push(`Más de ${OBRA_REF_MAX_NODOS} partidas.`);

  const porId = new Map<string, NodoObraReferencia>();
  for (const n of o.nodos) {
    if (porId.has(n.id)) errores.push(`Id repetido: ${n.id}`);
    porId.set(n.id, n);
    if (!n.nombre || n.nombre.trim() === '') errores.push(`Partida sin nombre: ${n.id}`);
  }
  for (const n of o.nodos) {
    if (n.padreId !== null && !porId.has(n.padreId)) errores.push(`El padre de ${n.id} no existe.`);
  }
  if (errores.length > 0) return errores;

  // Ciclos y profundidad: se sube por los padres con un tope de pasos.
  let hayCiclo = false;
  let maxProf = 0;
  for (const n of o.nodos) {
    let prof = 1;
    let cur: NodoObraReferencia | undefined = n;
    const visto = new Set<string>([n.id]);
    while (cur && cur.padreId !== null) {
      cur = porId.get(cur.padreId);
      if (!cur) break;
      if (visto.has(cur.id)) {
        hayCiclo = true;
        break;
      }
      visto.add(cur.id);
      prof++;
    }
    if (hayCiclo) break;
    maxProf = Math.max(maxProf, prof);
  }
  if (hayCiclo) {
    errores.push('El árbol tiene un ciclo.');
    return errores;
  }
  if (maxProf > OBRA_REF_MAX_PROFUNDIDAD) errores.push(`Profundidad ${maxProf} mayor que ${OBRA_REF_MAX_PROFUNDIDAD}.`);

  if (nodosElegibles(o).length === 0) errores.push('La obra no tiene ninguna partida donde poner conceptos.');
  return errores;
}

/** Nodos sin hijos, en orden DFS (respeta `orden`). Ya NO define las elegibles: ver `nodosElegibles`. */
export function hojasEnPreorden(o: ObraReferencia): NodoObraReferencia[] {
  const hijos = hijosPorPadre(o);
  const out: NodoObraReferencia[] = [];
  const visitar = (n: NodoObraReferencia) => {
    const hs = hijos.get(n.id);
    if (!hs || hs.length === 0) out.push(n);
    else for (const h of hs) visitar(h);
  };
  for (const r of hijos.get('') ?? []) visitar(r);
  return out;
}

/**
 * Partidas que pueden recibir conceptos, en orden DFS: TODOS los nodos (de cualquier nivel, también los que tienen
 * subpartidas) excepto la raíz única con hijos, que es la partida principal de la hoja. Con varias raíces no hay
 * partida principal y todas son elegibles.
 */
export function nodosElegibles(o: ObraReferencia): NodoObraReferencia[] {
  const hijos = hijosPorPadre(o);
  const raices = hijos.get('') ?? [];
  const raizUnica = raices.length === 1 && (hijos.get(raices[0]!.id)?.length ?? 0) > 0 ? raices[0]!.id : null;
  const out: NodoObraReferencia[] = [];
  const visitar = (n: NodoObraReferencia) => {
    if (n.id !== raizUnica) out.push(n);
    for (const h of hijos.get(n.id) ?? []) visitar(h);
  };
  for (const r of raices) visitar(r);
  return out;
}

/** Cómo m1 muestra una partida: «clave descripción» si trae clave (conserva ambos tal cual); si no, solo el nombre. */
export function etiquetaDeNodo(n: Pick<NodoObraReferencia, 'nombre' | 'clave'>): string {
  const c = n.clave?.trim();
  return c ? `${c} ${n.nombre}` : n.nombre;
}

/** nodoId → posición en el recorrido DFS (todos los nodos, no solo las hojas). */
export function ordenPreorden(o: ObraReferencia): Map<string, number> {
  const hijos = hijosPorPadre(o);
  const out = new Map<string, number>();
  let i = 0;
  const visitar = (n: NodoObraReferencia) => {
    out.set(n.id, i++);
    for (const h of hijos.get(n.id) ?? []) visitar(h);
  };
  for (const r of hijos.get('') ?? []) visitar(r);
  return out;
}

/**
 * Etiquetas («clave descripción») desde el nivel visible hasta el nodo. Regla ESTRUCTURAL: si el árbol tiene EXACTAMENTE una raíz y
 * esa raíz tiene hijos, se omite (en la hoja la partida general ya es el nombre del proyecto).
 */
export function rutaVisible(o: ObraReferencia, nodoId: string): string[] {
  const porId = new Map(o.nodos.map((n) => [n.id, n] as const));
  const raices = o.nodos.filter((n) => n.padreId === null);
  const raizUnicaConHijos = raices.length === 1 && o.nodos.some((n) => n.padreId === raices[0]!.id);
  const cadena: string[] = [];
  let cur = porId.get(nodoId);
  let pasos = 0;
  while (cur && pasos++ <= o.nodos.length) {
    if (!(raizUnicaConHijos && cur.id === raices[0]!.id)) cadena.push(etiquetaDeNodo(cur));
    cur = cur.padreId ? porId.get(cur.padreId) : undefined;
  }
  return cadena.reverse();
}

/** Profundidad visible: la ruta más larga entre las partidas elegibles. */
export function niveles(o: ObraReferencia): number {
  let max = 0;
  for (const h of nodosElegibles(o)) max = Math.max(max, rutaVisible(o, h.id).length);
  return max;
}

// ============ Partida de un renglón según una obra (lectura del cache) ============

export type PartidaDeRenglonPorObra =
  | { estado: 'asignada'; nodoId: string; ruta: string[]; orden: number }
  /** La IA dijo null, o el nodo ya no existe. */
  | { estado: 'sin_partida' }
  /** Sin entrada, o entrada vieja (huella/desc). */
  | { estado: 'sin_clasificar' };

/**
 * Qué partida de `obra` tiene un renglón, leyendo `agrupacion.obras[obra.id]`. Una entrada vale solo si su
 * huella es la de la obra actual y `desc` es la huella de la descripción actual (spec §3.2).
 */
export function partidaDeRenglonPorObra(
  agrupacion: PlanoAgrupacion | null,
  descripcion: string,
  obra: ObraReferenciaDTO,
  orden: Map<string, number>,
): PartidaDeRenglonPorObra {
  const e = agrupacion?.obras?.[obra.id];
  if (!e) return { estado: 'sin_clasificar' };
  if (e.huella !== obra.huella) return { estado: 'sin_clasificar' };
  if (e.desc !== huellaCorta(claveDeDescripcion(descripcion))) return { estado: 'sin_clasificar' };
  if (e.nodoId === null) return { estado: 'sin_partida' };
  const existe = obra.nodos.some((n) => n.id === e.nodoId);
  if (!existe) return { estado: 'sin_partida' };
  return {
    estado: 'asignada',
    nodoId: e.nodoId,
    ruta: rutaVisible(obra, e.nodoId),
    orden: orden.get(e.nodoId) ?? 0,
  };
}
